import type { AuditEvent } from '@/lib/types';

const ACTIONS: Record<string, string> = {
  'case.created': 'Recorded the case',
  'case.reversed': 'Reversed the case',
  'case.entries_added': 'Added details to the case',
  'case.viewed': 'Viewed the case',
  'entry.amended': 'Amended an entry',
  'entry.ended': 'Ended an entry early',
  'entry.removed': 'Removed an entry',
  'entry.viewed': 'Viewed an entry',
  'register.searched': 'Searched the register',
  'register.listed': 'Listed the register',
  'document.uploaded': 'Uploaded a document',
  'document.downloaded': 'Downloaded a document',
  'document.removed': 'Removed a document',
  'import.validated': 'Checked a backfill file',
  'import.completed': 'Imported a backfill file',
  'import.rejected': 'Backfill file rejected',
  'retention.purged': 'Removed expired records (six-year retention)',
  'user.login_failed': 'Failed sign-in',
  'user.locked': 'Account locked after failed attempts',
  'user.signed_in': 'Signed in',
  'user.signed_out': 'Signed out',
  'user.mfa_enrolled': 'Set up two-step verification',
  'user.recovery_code_used': 'Used a recovery code',
  'user.recovery_codes_regenerated': 'Replaced recovery codes',
  'user.invited': 'Invited a user',
  'user.invite_accepted': 'Accepted an invitation',
  'user.updated': 'Updated a user',
  'user.disabled': 'Disabled a user',
  'user.enabled': 'Re-enabled a user',
  'user.mfa_reset': 'Reset a user’s two-step verification',
  'user.password_changed': 'Changed their password',
  'user.password_reset_requested': 'Password reset requested',
  'user.password_reset': 'Reset their password',
  'client.created': 'Created a client',
  'client.updated': 'Updated a client',
  'client.key_added': 'Added an API key',
  'client.key_revoked': 'Revoked an API key',
};

export function describeAction(action: string): string {
  return ACTIONS[action] ?? action;
}

export const AUDIT_ACTIONS = Object.keys(ACTIONS);

/** A short extra detail for well-known event shapes. */
export function auditDetail(e: AuditEvent): string | null {
  const d = (e.data ?? {}) as Record<string, unknown>;
  if (e.action === 'register.searched' && typeof d.q === 'string') return `“${d.q}”: ${d.resultCount ?? 0} result(s)`;
  if (e.action === 'case.created' && Array.isArray(d.entries)) return `${d.entries.length} detail(s)`;
  if (e.action === 'case.entries_added' && Array.isArray(d.entries)) return `${d.entries.length} detail(s)`;
  if (e.action === 'entry.amended' && d.before && d.after) {
    const b = d.before as Record<string, string>;
    const a = d.after as Record<string, string>;
    const parts = [];
    if (b.value !== a.value) parts.push(`${b.value} → ${a.value}`);
    if (b.entryType !== a.entryType) parts.push('entry type changed');
    return parts.join('; ') || null;
  }
  if (e.action.startsWith('document.') && typeof d.filename === 'string') return d.filename;
  if (e.action.startsWith('import.') && typeof d.filename === 'string') return `${d.filename}: ${d.totalRows ?? '?'} row(s)`;
  if (e.action === 'user.invited' && typeof d.email === 'string') return `${d.email}${d.resent ? ' (resent)' : ''}`;
  if (e.action === 'user.login_failed' && typeof d.reason === 'string') return d.reason;
  if (e.action === 'user.signed_in' && typeof d.method === 'string') return `with ${String(d.method).replace('_', ' ')}`;
  if (e.action === 'client.key_added' && typeof d.kid === 'string') return `key ${d.kid}`;
  return null;
}
